/**
 * Glyphs for Teri's dock position and its matching dismiss action.
 *
 * Heroicons has nothing that reads as "floating vs sidebar", so these are
 * hand-drawn on one grammar: an outlined viewport with a SOLID block showing
 * where the chat sits inside it. Only the block moves between the two, which
 * is what makes the menu legible at a glance — the position IS the icon.
 *
 * The two dismiss glyphs mirror the same idea: from the floating corner the
 * chat collapses DOWN to its launcher; from the rail it slides out to the
 * RIGHT edge. Same 24x24 grid and 1.6 stroke as the surrounding heroicons.
 */

interface IconProps {
  className?: string;
}

/** Viewport outline shared by the three panel glyphs. */
const VIEWPORT = { x: 2.75, y: 3.75, width: 18.5, height: 16.5, rx: 3 };

const base = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  xmlns: 'http://www.w3.org/2000/svg',
} as const;

/** Chat parked in the lower-right corner, clear of every edge. */
export function DockFloatingIcon({ className }: IconProps) {
  return (
    <svg {...base} className={className} aria-hidden="true">
      <rect {...VIEWPORT} />
      <rect
        x="11.5"
        y="11.5"
        width="7"
        height="6"
        rx="1.75"
        fill="currentColor"
        stroke="none"
      />
    </svg>
  );
}

/** Chat as a full-height rail hugging the right edge. */
export function DockSidebarIcon({ className }: IconProps) {
  return (
    <svg {...base} className={className} aria-hidden="true">
      <rect {...VIEWPORT} />
      <rect
        x="14.5"
        y="4.75"
        width="5.75"
        height="14.5"
        rx="1.5"
        fill="currentColor"
        stroke="none"
      />
    </svg>
  );
}

/** Minimize: the floating panel drops back down into its launcher. */
export function MinimizeDownIcon({ className }: IconProps) {
  return (
    <svg {...base} className={className} aria-hidden="true">
      <path
        d="M8.5 9.25 12 12.75l3.5-3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M7.25 16.75h9.5" strokeLinecap="round" />
    </svg>
  );
}

/** Close the rail: it slides out past the right edge. */
export function CollapseRightIcon({ className }: IconProps) {
  return (
    <svg {...base} className={className} aria-hidden="true">
      <rect {...VIEWPORT} />
      <path d="M14.75 3.75v16.5" />
      <path
        d="m7 9.25 3 2.75-3 2.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
